import { useDispatch, useSelector } from 'react-redux';
import GoogleAuthenticatorService from '../services/TwoFactorAuthService';
import {
  setQrGenerated,
  setTwoFactorEnabled,
  setLoading,
  setCheckingStatus,
  setSecret,
  verifyCodeSuccess,
  verifyCodeFailure,
  reset2FA,
  setError
} from '../store/slices/twoFactorAuth/twoFactorAuthSlice';
import type { RootState, AppDispatch } from '../store/store';
import { IVerifyCodeResponse } from '../types/googleAuth/googleAuthInterfaces';
import { useRecoveryCodes } from './useRecoveryCodes';

export const useTwoFactorAuth = () => {
  const dispatch: AppDispatch = useDispatch();
  const { qrGenerated, twoFactorEnabled, secret, otpAuthUrl, isValid, loading, checkingStatus, error } = useSelector(
    (state: RootState) => state.twoFactorAuth
  );
  const { generateRecoveryCodes } = useRecoveryCodes();

  const generateSecret = async (email: string) => {
    dispatch(setLoading(true));
    try {
      const response = await GoogleAuthenticatorService.generateSecret(email);
      console.log('generateSecret', response);
      dispatch(setSecret(response));
      dispatch(setQrGenerated(true))
    } catch (error: any) {
      dispatch(setError(error.message || 'Error al generar el código QR'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const verifyCode = async (email: string, code: string) => {
    try {
        const response: IVerifyCodeResponse = await GoogleAuthenticatorService.verifyCode(email, code);
        console.log('verifyCode', response);

        if (response.activated) {
            console.log('✅ Antes de generar códigos de recuperación');
            await generateRecoveryCodes(email);
            console.log('✅ Códigos de recuperación generados con éxito');

            dispatch(setTwoFactorEnabled(response.activated));
            dispatch(verifyCodeSuccess(response));
            dispatch(setQrGenerated(false));
        }
    } catch (error: any) {
        console.error('❌ Error en verifyCode:', error);
        dispatch(verifyCodeFailure(error.message || 'Código incorrecto'));
    }
};


  const disable2FA = async (email: string) => {
    dispatch(setLoading(true));
    try {
      await GoogleAuthenticatorService.disable2FA(email);
      dispatch(reset2FA());
      dispatch(setTwoFactorEnabled(false))
      dispatch(setQrGenerated(false))
    } catch (error: any) {
      dispatch(setError(error.message || 'Error al desactivar 2FA'));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const check2FAStatus = async (email: string) => {
    dispatch(setCheckingStatus(true));
    try {
      const response = await GoogleAuthenticatorService.check2FAStatus(email);
      // console.log('check2FAStatus', response);
      dispatch(setTwoFactorEnabled(response.enabled))
    } catch (error: any) {
      dispatch(setError(error.message || 'Error al verificar 2FA'));
    } finally {
      dispatch(setCheckingStatus(false));
    }
  };

  return {
    qrGenerated,
    twoFactorEnabled,
    secret,
    otpAuthUrl,
    isValid,
    loading,
    checkingStatus,
    error,
    generateSecret,
    verifyCode,
    disable2FA,
    check2FAStatus,
  };
};
